import {
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
	ElementRef,
	Input,
	Output,
	EventEmitter
} from '@angular/core';
import {
	trigger,
	state,
	style,
	transition,
	animate,
	query,
	stagger
} from '@angular/animations';
import { CustomerService } from '../../services/customer.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormControl, FormGroup, FormControl } from '@angular/forms';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

@Component({
	selector: 'app-new-customers',
	templateUrl: './new-customers.component.html',
	styleUrls: ['./new-customers.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({ transform: 'rotate3d(0, 1, 0, -179deg)' })),
			state('inactive', style({ transform: 'rotateY(0)' })),
			state('orderlookup', style({ transform: 'rotateY(-179deg)' })),
			transition('active <=> inactive', animate('500ms ease')),
			transition('inactive <=> orderlookup', animate('500ms ease'))
		]),
		trigger('openClose', [
			state(':enter', style({ height: '*' })),
			state(':leave', style({ height: '0px' })),
			transition('false <=> true', animate(500))
		]),
		trigger('filterAnimation', [
			transition(':increment', [
				query(':enter', [
					style({ opacity: 0, width: 0 }),
					stagger(50, [animate('300ms ease-out', style({ opacity: 1, width: '*' }))])
				], { optional: true })
			]),
			transition(':decrement', [
				query(':leave', [
					stagger(50, [animate('300ms ease-out', style({ opacity: 0, width: 0 }))])
				])
			])
		]),
		trigger('grow', [
			transition(':enter', [
				style({ height: '0', width: '0' }),
				animate(200, style({ height: '*', width: '*' })),
				animate('200ms', style({ opacity: 1 }))
			]),
			transition(':leave', [
				animate(100, style({ height: 0, width: 0 })),
				animate('100ms', style({ opacity: 0 }))
			])
		])
	]
})
export class NewCustomersComponent implements OnInit, OnDestroy {
	@Input() flip = 'inactive';
	@Output() viewData = new EventEmitter < any > ();
	@ViewChild('monthDetails') monthDetailsRef: ElementRef;
	@ViewChild('print_table') printtable: ElementRef;

	fromdate: Date;
	todate: Date;
	searchTerm = '';
	sortColumn = '';
	sortDirection: 'asc' | 'desc' = 'asc';
	filteredData: any[] = [];
	data: any = false;
	total = 0;
	title = 'New Customers';
	sending = false;
	viewing = false;
	datasub: any = false;

	hideshowunits = new UntypedFormControl(true);
	hideshowsales = new UntypedFormControl(true);
	hideshowcost = new UntypedFormControl(false);
	defaultlocation = new UntypedFormControl('');
	user: any = false;
	ref_location: any = false;
	filename = '';
	
	range: FormGroup;
	  
	constructor(
		private printService: PrintService,
		public globalSearchService: GlobalSearchService,
		private customerService: CustomerService,
		private modalService: NgbModal
	) {}

	ngOnInit(): void {
		const now = new Date();
		const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
	
		this.range = new FormGroup({
			start: new FormControl(firstDayPrevMonth),
			end: new FormControl(now),
		});
	
		const updateTitles = () => {
			const startStr = this.range.controls.start.value?.toLocaleDateString() || '';
			const endStr = this.range.controls.end.value?.toLocaleDateString() || '';
			this.filename = `New Customers ${startStr} - ${endStr}`;
			this.title = `New Customers ${startStr} - ${endStr}`;
		};
	
		updateTitles();
		this.loadData();
	
		this.range.valueChanges.subscribe(() => {
			updateTitles();
		});
	
		this.globalSearchService.user.subscribe(results => {
			if (this.user && results.user.defaultlocation.loccode !== this.ref_location) {
				this.defaultlocation.setValue(this.user.user.defaultlocation.loccode);
				this.ref_location = false;
			}
	
			this.user = results;
			if (!this.ref_location) {
				this.ref_location = this.user.user.defaultlocation.loccode;
			}
		});
	}


	onSearchChange(): void {
		const term = this.searchTerm.toLowerCase();
		if (!term) {
			this.filteredData = this.data;
			return;
		}

		this.filteredData = this.data.filter((item: any) =>
			(item.debtorno?.toString().toLowerCase().includes(term)) ||
			(item.name?.toLowerCase().includes(term)) ||
			(item.address1?.toLowerCase().includes(term)) ||
			(item.address3?.toLowerCase().includes(term)) ||
			(item.address4?.toLowerCase().includes(term)) ||
			(item.address5?.toLowerCase().includes(term)) ||
			(item.salesmanname?.name?.toLowerCase().includes(term))
		);
	}

	loadData() {
		
		const data = {
			fromdate: this.range.get('start').value,
			todate: this.range.get('end').value
		};

		this.sending = true;

		this.datasub = this.customerService.getNewCustomers(data).subscribe((r: any) => {
			this.total = r.length;
			this.data = r;
			this.filteredData = [...this.data];
			this.onSearchChange();
			this.sending = false;
		});
	}

	toggleFlip(data: any, title: string) {
		const prev = this.flip;
		this.flip = data ? (this.flip === 'active' ? 'inactive' : 'active') : 'inactive';
	
		if (this.flip === 'active') {
			if (prev !== 'active') {
				this.viewing = data;
				this.title = title;
				setTimeout(() => {
					this.viewData.emit({ data, title: this.title , comp: 'newcustomer' });
				}, 0);
			}
		} else {
			setTimeout(() => {
				this.viewData.emit(false);
			}, 0);
		}
	}

	exportPdf() {
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const timestamp = new Date().toLocaleString();
	
		const data = {
			content: encoded,
			filename: this.filename,
			title: this.title,
			subtitle: `Created: ${timestamp}`,
			landscape: true
		};
	
		this.printService.pdf(data).subscribe((result: any) => {
			this.sending = false;
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}
	
	exportXls() {
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const timestamp = new Date().toLocaleString();
	
		const data = {
			content: encoded,
			filename: this.filename,
			title: this.title,
			subtitle: `Created: ${timestamp}`
		};
	
		this.printService.xls(data).subscribe((result: any) => {
			this.sending = false;
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}


	sort(column: string): void {
		if (this.sortColumn === column) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}

		this.filteredData.sort((a: any, b: any) => {
			const aValue = (a[column] ?? '').toString().toLowerCase();
			const bValue = (b[column] ?? '').toString().toLowerCase();

			if (!isNaN(+aValue) && !isNaN(+bValue)) {
				return this.sortDirection === 'asc' ? +aValue - +bValue : +bValue - +aValue;
			}

			return this.sortDirection === 'asc' ?
				aValue.localeCompare(bValue) :
				bValue.localeCompare(aValue);
		});
	}

	ngOnDestroy() {
		if (this.datasub) {
			this.datasub.unsubscribe();
		}
	}
}